import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './DeveloperNotesPage.css';

const DeveloperNotesPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [editingId, setEditingId] = useState('');
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [taskActionState, setTaskActionState] = useState({});
  const [form, setForm] = useState({
    title: '',
    version: '',
    changes: '',
    tagsText: '',
    isPublished: true,
    isPinned: false,
    targetAudience: 'staff'
  });

  const isAdmin = currentUser?.userType === 'admin';
  const isStaff = currentUser?.userType === 'staff';
  const isDev = currentUser?.userType === 'dev';

  useEffect(() => {
    api.get('/api/connector/config')
      .then((res) => {
        setConnectorConfig(res.data.config);
        setDefaultComsappBaseUrl(res.data.defaultComsappBaseUrl || '');
      })
      .catch(() => {/* not critical */});
  }, []);

  const formatDate = (value) => {
    if (!value) return 'No date';
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAuthorName = (note) => {
    if (!note?.createdBy) return 'Unknown';
    const first = note.createdBy.firstName || '';
    const last = note.createdBy.lastName || '';
    const name = `${first} ${last}`.trim();
    return name || note.createdBy.email || 'Unknown';
  };

  const resetForm = () => {
    setEditingId('');
    setForm({
      title: '',
      version: '',
      changes: '',
      tagsText: '',
      isPublished: true,
      isPinned: false,
      targetAudience: isStaff ? 'dev' : 'staff'
    });
  };

  const parseTags = (value) => {
    if (!value) return [];
    return [...new Set(value
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean))];
  };

  const fetchNotes = async () => {
    setLoading(true);
    setError('');

    try {
      let query = '';

      if (isAdmin) {
        query = '?includeUnpublished=true';
      } else if (isStaff) {
        query = '?sentToDev=true';
      }

      const response = await api.get(`/api/developer-notes${query}`);
      setNotes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    resetForm();
  }, [currentUser?.userType]);

  useEffect(() => {
    if (!isDev) return;
    const userId = currentUser?._id || currentUser?.id || 'unknown';
    localStorage.setItem(`devNotesLastSeenAt:${userId}`, new Date().toISOString());
  }, [isDev, currentUser?._id, currentUser?.id]);

  useEffect(() => {
    fetchNotes();
  }, [currentUser?.userType]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setFormError('');

    const payload = {
      title: form.title.trim(),
      version: form.version.trim(),
      changes: form.changes.trim(),
      tags: parseTags(form.tagsText),
      isPublished: form.isPublished,
      isPinned: isStaff ? false : form.isPinned,
      targetAudience: isStaff ? 'dev' : form.targetAudience
    };

    if (!payload.title || !payload.changes) {
      setFormError('Title and note content are required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/developer-notes/${editingId}`, payload);
      } else {
        await api.post('/api/developer-notes', payload);
      }

      resetForm();
      await fetchNotes();
    } catch (err) {
      console.error('Error saving note:', err);
      setFormError(err.response?.data?.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    if (!isAdmin) return;

    setEditingId(note._id);
    setForm({
      title: note.title || '',
      version: note.version || '',
      changes: note.changes || '',
      tagsText: Array.isArray(note.tags) ? note.tags.join(', ') : '',
      isPublished: Boolean(note.isPublished),
      isPinned: Boolean(note.isPinned),
      targetAudience: note.targetAudience || 'staff'
    });
    setFormError('');
  };

  const toggleNoteField = async (note, field) => {
    if (!isAdmin) return;

    setFormError('');
    try {
      await api.put(`/api/developer-notes/${note._id}`, { [field]: !note[field] });
      await fetchNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      setFormError('Failed to update note');
    }
  };

  const deleteNote = async (noteId) => {
    if (!isAdmin) return;

    if (!window.confirm('Delete this note?')) return;

    try {
      await api.delete(`/api/developer-notes/${noteId}`);
      if (editingId === noteId) resetForm();
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      setFormError('Failed to delete note');
    }
  };

  const createTaskFromNote = async (noteId) => {
    setTaskActionState((prev) => ({ ...prev, [noteId]: { loading: true } }));
    try {
      const response = await api.post('/api/connector/tasks/from-note', { noteId });
      setTaskActionState((prev) => ({
        ...prev,
        [noteId]: {
          loading: false,
          success: true,
          deepLink: response.data?.deepLink || '',
        },
      }));
    } catch (err) {
      setTaskActionState((prev) => ({
        ...prev,
        [noteId]: {
          loading: false,
          success: false,
          message: err.response?.data?.message || 'Failed to create task in ComSapp',
        },
      }));
    }
  };

  const buildChannelSpaceUrl = (channelKey) => {
    const channel = connectorConfig?.channels?.[channelKey];
    if (!channel?.enabled || !channel?.slug || !connectorConfig?.comsappBaseUrl) return '';

    const baseUrl = (connectorConfig.comsappBaseUrl || defaultComsappBaseUrl || '').replace(/\/$/, '');
    if (!baseUrl) return '';
    return channel.spaceId
      ? `${baseUrl}/spaces/${channel.spaceId}`
      : `${baseUrl}/spaces?slug=${channel.slug}`;
  };

  // A channel is "ready" (can create tasks) if it is enabled and has a slug/id — no comsappBaseUrl needed for the action itself
  const isChannelReady = (channelKey) => {
    const channel = connectorConfig?.channels?.[channelKey];
    return Boolean(channel?.enabled && (channel?.slug || channel?.spaceId));
  };

  const devChannel = connectorConfig?.channels?.dev;
  const staffChannel = connectorConfig?.channels?.staff;
  const devSpaceUrl = buildChannelSpaceUrl('dev');
  const staffSpaceUrl = buildChannelSpaceUrl('staff');
  const devChannelReady = isChannelReady('dev');
  const useComsappForDev = Boolean(devSpaceUrl);
  const canCreate = isAdmin || (isStaff && !useComsappForDev);

  const title = isStaff
    ? useComsappForDev ? 'Dev Team Messaging' : 'Send Note To Dev Team'
    : isDev
      ? 'Notes From Staff'
      : 'Developer Notes';

  const subtitle = isStaff
    ? useComsappForDev
      ? 'Live dev communication now runs through ComSapp. Use this page for context, archive, and release notes.'
      : 'Staff can send internal implementation notes directly to the dev team.'
    : isDev
      ? useComsappForDev
        ? 'Use ComSapp for live conversation. This page remains the shared archive for release context and note history.'
        : 'Messages sent from staff to the dev team.'
      : 'Manage internal notes for staff and dev audiences.';

  const availableTags = [...new Set(notes
    .flatMap((note) => Array.isArray(note.tags) ? note.tags : [])
    .map((tag) => String(tag).trim())
    .filter(Boolean))].sort();

  const filteredNotes = activeTag === 'all'
    ? notes
    : notes.filter((note) => Array.isArray(note.tags) && note.tags.includes(activeTag));

  const devSpaceName = devChannel?.spaceName || devChannel?.label || 'Dev Space';
  const staffSpaceName = staffChannel?.spaceName || staffChannel?.label || 'Staff Space';

  return (
    <div className="developer-notes-page">
      <div className="developer-notes-header">
        <h1>{title}</h1>
        <p>{subtitle}</p>
        <Link to={isAdmin ? '/admin-dashboard' : '/account'} className="back-link">← Back</Link>
      </div>

      {devSpaceUrl && (
        <div className="developer-notes-card comsapp-integration-card">
          <h2>Live Thread Space</h2>
          <p>
            Use ComSapp for real-time threaded discussion and task follow-up.
            Keep this page for app-specific tracking, archive, tags, and release context.
          </p>
          <div className="dev-note-actions">
            <a href={devSpaceUrl} target="_blank" rel="noreferrer" className="back-link">
            Open {devSpaceName} in ComSapp
            </a>
            {staffSpaceUrl && (
              <a href={staffSpaceUrl} target="_blank" rel="noreferrer" className="back-link">
                Open {staffSpaceName}
              </a>
            )}
          </div>
        </div>
      )}

      {isStaff && useComsappForDev && (
        <div className="developer-notes-card">
          <h2>ComSapp Is Now The Primary Dev Channel</h2>
          <p>
            Staff-to-dev communication has been moved to the message system. Post live questions,
            blockers, and implementation discussion in the Dev space. Use the Staff space for staff/admin
            coordination when a reply loop should stay internal.
          </p>
        </div>
      )}

      {error && <div className="note-error">{error}</div>}

      {canCreate && (
        <div className="developer-notes-card">
          <h2>{editingId ? 'Edit Note' : isStaff ? 'New Note To Dev' : 'New Note'}</h2>

          <form className="developer-note-form" onSubmit={handleSave}>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              placeholder="Title"
              maxLength={160}
              required
            />

            <input
              type="text"
              name="version"
              value={form.version}
              onChange={handleInputChange}
              placeholder="Version (optional)"
              maxLength={60}
            />

            <textarea
              name="changes"
              value={form.changes}
              onChange={handleInputChange}
              rows={6}
              maxLength={8000}
              placeholder="Write your note..."
              required
            />

            <input
              type="text"
              name="tagsText"
              value={form.tagsText}
              onChange={handleInputChange}
              placeholder="Tags (comma-separated): progress, issue, blocker, fix"
              maxLength={320}
            />

            <div className="note-flags">
              <label>
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={form.isPublished}
                  onChange={handleInputChange}
                />
                Published
              </label>

              {!isStaff && (
                <label>
                  <input
                    type="checkbox"
                    name="isPinned"
                    checked={form.isPinned}
                    onChange={handleInputChange}
                  />
                  Pin to top
                </label>
              )}
            </div>

            {isAdmin && (
              <div className="note-audience">
                <label htmlFor="targetAudience">Audience</label>
                <select
                  id="targetAudience"
                  name="targetAudience"
                  value={form.targetAudience}
                  onChange={handleInputChange}
                >
                  <option value="staff">Staff</option>
                  <option value="dev">Dev</option>
                </select>
              </div>
            )}

            {formError && <p className="note-error">{formError}</p>}

            <div className="note-actions">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Note' : 'Send Note'}
              </button>
              {editingId && (
                <button type="button" className="secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="developer-notes-card">
        <h2>{isStaff ? 'Your Sent Dev Notes' : 'Notes'}</h2>

        {availableTags.length > 0 && (
          <div className="note-filter-row">
            <label htmlFor="noteTagFilter">Filter by tag</label>
            <select
              id="noteTagFilter"
              value={activeTag}
              onChange={(event) => setActiveTag(event.target.value)}
            >
              <option value="all">All tags</option>
              {availableTags.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <p>Loading notes...</p>
        ) : filteredNotes.length === 0 ? (
          <p>No notes found.</p>
        ) : (
          <div className="notes-list">
            {filteredNotes.map((note) => (
              <article key={note._id} className="note-item">
                <div className="note-item-header">
                  <div>
                    <h3>{note.title}</h3>
                    <p className="note-meta">
                      {note.version ? `${note.version} • ` : ''}
                      {formatDate(note.publishedAt || note.createdAt)} by {getAuthorName(note)}
                    </p>
                  </div>

                  <div className="note-badges">
                    {note.isPinned && <span className="badge pinned">Pinned</span>}
                    <span className="badge audience">{note.targetAudience === 'dev' ? 'Dev' : 'Staff'}</span>
                    <span className={`badge ${note.isPublished ? 'published' : 'draft'}`}>
                      {note.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>

                <p className="note-body">{note.changes}</p>

                {Array.isArray(note.tags) && note.tags.length > 0 && (
                  <div className="note-tags">
                    {note.tags.map((tag) => (
                      <span key={`${note._id}-${tag}`} className="note-tag">#{tag}</span>
                    ))}
                  </div>
                )}

                {isAdmin && (
                  <div className="note-item-actions">
                    <button type="button" onClick={() => startEdit(note)}>Edit</button>
                    <button type="button" onClick={() => toggleNoteField(note, 'isPublished')}>
                      {note.isPublished ? 'Unpublish' : 'Publish'}
                    </button>
                    <button type="button" onClick={() => toggleNoteField(note, 'isPinned')}>
                      {note.isPinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button type="button" className="danger" onClick={() => deleteNote(note._id)}>
                      Delete
                    </button>
                  </div>
                )}

                {devChannelReady && (isStaff || isAdmin) && (
                  <div className="note-item-actions">
                    <button
                      type="button"
                      onClick={() => createTaskFromNote(note._id)}
                      disabled={Boolean(taskActionState[note._id]?.loading)}
                    >
                      {taskActionState[note._id]?.loading ? 'Creating Task...' : 'Create ComSapp Task'}
                    </button>
                    {taskActionState[note._id]?.success && taskActionState[note._id]?.deepLink && (
                      <a href={taskActionState[note._id].deepLink} target="_blank" rel="noreferrer" className="back-link">
                        Open Task
                      </a>
                    )}
                    {taskActionState[note._id]?.success === false && (
                      <span className="note-error">{taskActionState[note._id].message}</span>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperNotesPage;
